/**
 * Aritmética inteira para valores monetários.
 *
 * O Firestore guarda número como double de 64 bits e não tem decimal exato.
 * Num sistema que decide sobre margem de 15,67%, deriva de ponto flutuante
 * não é aceitável. A convenção do projeto (seção 7.1 do planejamento):
 *
 *   dinheiro    -> centavos inteiros        sufixo Cents  (3890  = R$ 38,90)
 *   percentual  -> pontos-base inteiros     sufixo Bps    (1400  = 14,00%)
 *   quantidade  -> milésimos inteiros       sufixo Milli  (98000 = 98,000 un)
 *
 * Todo cálculo é aritmética inteira com pontos de arredondamento explícitos.
 */

export const BPS = 10_000;
export const MILLI = 1_000;

/** Arredonda meio para cima, afastando do zero. Espelha o ROUND_HALF_UP usado na conferência do planejamento. */
export function arredondar(numerador: number, denominador: number): number {
  if (denominador === 0) throw new Error('divisão por zero em arredondar()');
  const negativo = numerador * denominador < 0;
  const n = Math.abs(numerador);
  const d = Math.abs(denominador);
  const r = Math.floor((n + d / 2) / d);
  return negativo ? -r : r;
}

/** Aplica um percentual em pontos-base sobre um inteiro. aplicarBps(8400, 1400) = 1176 */
export function aplicarBps(valor: number, bps: number): number {
  return arredondar(valor * bps, BPS);
}

/** Multiplica um valor unitário por uma quantidade em milésimos. porQuantidade(3890, 100000) = 389000 */
export function porQuantidade(valorUnitario: number, quantidadeMilli: number): number {
  return arredondar(valorUnitario * quantidadeMilli, MILLI);
}

/** Divide um total por uma quantidade em milésimos, devolvendo valor unitário. porUnidade(422338, 98000) = 4310 */
export function porUnidade(total: number, quantidadeMilli: number): number {
  if (quantidadeMilli === 0) throw new Error('quantidade zero em porUnidade()');
  return arredondar(total * MILLI, quantidadeMilli);
}

/** Razão entre dois inteiros expressa em pontos-base. emBps(1316, 8400) = 1567 (15,67%) */
export function emBps(parte: number, total: number): number {
  if (total === 0) throw new Error('total zero em emBps()');
  return arredondar(parte * BPS, total);
}

/** Formata centavos para exibição em reais. formatarReais(422338) = "R$ 4.223,38" */
export function formatarReais(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Formata pontos-base para exibição percentual. formatarPct(1567) = "15,67%" */
export function formatarPct(bps: number, casas = 2): string {
  return `${(bps / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  })}%`;
}

/** Formata quantidade em milésimos. formatarQtd(98000) = "98" */
export function formatarQtd(milli: number): string {
  return (milli / MILLI).toLocaleString('pt-BR', { maximumFractionDigits: 3 });
}
